import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Timesheets (PART 13, PRJ-03) — the smallest and most important record in
 * the module: everything downstream (task actuals, consumption, every
 * invoice for time) depends on it.
 *
 * The rule that shapes the screen: APPROVAL LOCKS THE WEEK. Submitted and
 * approved weeks are locked in the interface AND the data layer
 * (BR-PRJ-03-05 — the DB trigger is the floor). Owner ruling R8
 * (BR-PRJ-03-16): a DRAFT entry is freely editable and deletable; an
 * approved one is never rewritten and never deleted — corrections are
 * ADJUSTING ENTRIES (± hours, a reason, the period they fix; land already
 * approved; excluded from billing, BR-PRJ-03-10/11).
 *
 * The ruled billable fix (BR-PRJ-03-03): the flag is an explicit input on
 * every entry point. Its default is the PROJECT's own billable setting —
 * the server supplies it when the request leaves it out — and the caller
 * may override it either way. No entry point stores a value the user could
 * not see.
 *
 * The week (§13.1.5): Sunday–Saturday; working days Sunday–Thursday.
 * Status ladder (§5c/§7): draft → submitted → approved (locked forever), or
 * rejected back to draft with the comment stamped on every entry
 * (BR-PRJ-03-08). A person must not approve their own week (BR-PRJ-03-14).
 */
// ── Vocabularies ────────────────────────────────────────────────────────────
export const WEEK_STATUSES = ['draft', 'submitted', 'approved', 'rejected'];
/** Positive hours, at most 2 decimals, ≤ 24 (one day). */
export const HoursStringSchema = z
    .string()
    .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Hours are decimal strings with at most 2 decimals')
    .refine((value) => {
    const [integer = ''] = value.split('.');
    return Number.parseInt(integer, 10) <= 24;
}, 'A day has at most 24 hours.');
/** ± hours for an adjusting entry — never zero. */
export const SignedHoursStringSchema = z
    .string()
    .regex(/^-?\d{1,2}(\.\d{1,2})?$/, 'Hours are decimal strings with at most 2 decimals')
    .refine((value) => !/^-?0+(\.0+)?$/.test(value), 'An adjustment must move the hours.');
// ── The fixed sentences ─────────────────────────────────────────────────────
export const TIMESHEET_MESSAGES = {
    /** §5d's padlock note, served with a locked week — ERR-PRJ-03-01's grey cells, with their reason. */
    locked: 'This period is locked (submitted/approved). Corrections require an adjusting entry.',
    /** BR-PRJ-03-07's confirmation, word for word. */
    approveConfirm: 'Approving locks this timesheet period — its entries become immutable and feed billable Actual Hours. This cannot be undone from here.',
    /** The §13 walkthrough's post-approval report. */
    approved: 'Approved & locked — Actual Hours updated on linked tasks',
    /** BR-PRJ-03-08 / ERR-PRJ-03-03 — the prototype's Reject & return did nothing and said nothing. */
    rejectNeedsComment: 'A comment is required; entries return to Draft for correction.',
    /** BR-PRJ-03-14. */
    selfApprove: 'You cannot approve your own week.',
    /** BR-PRJ-03-06: submit needs at least one draft entry. */
    nothingToSubmit: 'There are no draft entries in this week to submit.',
    alreadySubmitted: 'This week is already submitted or approved.',
    notSubmitted: 'Only a submitted week can be approved or rejected.',
    /** BR-PRJ-03-01. */
    incomplete: 'An entry needs a project, a task, a date and hours.',
    /** PRJ-03 §15: an entry on a locked week. */
    weekLocked: 'This week is locked — ask the manager to reject it back to draft.',
    /**
     * BR-PRJ-03-10's intended wording, kept — and ERR-PRJ-03-05, which is
     * somebody looking for a way to open an adjusting entry and finding none.
     * There is one now, and this sentence is where it is named.
     */
    adjustingHint: 'Approved periods are locked. Post a new adjusting entry (+/− hours) referencing the original period.',
    adjustNeedsApproved: 'An adjusting entry must reference a submitted or approved period.',
    /** PRJ-03 §5e's empty state (the never-rendered message, rendered). */
    emptyWeek: 'No entries this week.',
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const WeekStatusSchema = z.enum(WEEK_STATUSES);
export const TimesheetEntrySchema = z
    .object({
    id: z.uuid(),
    userId: z.uuid(),
    userName: z.string().nullable(),
    projectId: z.uuid(),
    projectName: z.string(),
    /** The project's own default the entry inherited or overrode (§5b). */
    projectBillable: z.boolean(),
    taskId: z.uuid(),
    taskTitle: z.string(),
    workDate: IsoDateSchema,
    weekStart: IsoDateSchema,
    /** Signed only on adjusting entries. */
    hours: z.string(),
    billable: z.boolean(),
    status: z.enum(['draft', 'submitted', 'approved']),
    notes: z.string().nullable(),
    /** BR-PRJ-03-08: stamped on every entry of a rejected week. */
    rejectionComment: z.string().nullable(),
    /** Fields 10/11: set by billing; once on, never billable again. */
    invoiced: z.boolean(),
    invoiceId: z.uuid().nullable(),
    invoiceCode: z.string().nullable(),
    /** Field 12: a correction — excluded from billing by design. */
    adjusting: z.boolean(),
    adjustsWeekStart: IsoDateSchema.nullable(),
    adjustReason: z.string().nullable(),
})
    .strict();
export const WeekResponseSchema = z
    .object({
    userId: z.uuid(),
    userName: z.string().nullable(),
    weekStart: IsoDateSchema,
    /** §5c's four-step derivation (the week row + its entries). */
    status: WeekStatusSchema,
    /** §5d: submitted or approved. */
    locked: z.boolean(),
    rejectionComment: z.string().nullable(),
    entries: TimesheetEntrySchema.array(),
    totalHours: z.string(),
    billableHours: z.string(),
    nonBillableHours: z.string(),
    /** §5e: working days (Sun–Thu), not future, zero hours. */
    missingDays: IsoDateSchema.array(),
})
    .strict();
export const ApprovalRowSchema = z
    .object({
    weekId: z.uuid(),
    userId: z.uuid(),
    userName: z.string().nullable(),
    weekStart: IsoDateSchema,
    totalHours: z.string(),
    billableHours: z.string(),
    nonBillableHours: z.string(),
})
    .strict();
export const UpsertCellRequestSchema = z
    .object({
    projectId: z.uuid(),
    taskId: z.uuid(),
    workDate: IsoDateSchema,
    /** '0' deletes the entry (BR-PRJ-03-02). */
    hours: z
        .string()
        .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Hours are decimal strings with at most 2 decimals'),
    /**
     * BR-PRJ-03-03: omitted = the PROJECT's default (a pre-fill the form
     * shows); present = the user's explicit choice, honoured as given.
     */
    billable: z.boolean().optional(),
    notes: z.string().trim().max(500).optional(),
})
    .strict();
export const UpdateEntryRequestSchema = z
    .object({
    hours: HoursStringSchema.optional(),
    billable: z.boolean().optional(),
    notes: z.string().trim().max(500).optional(),
})
    .strict();
export const SubmitWeekRequestSchema = z
    .object({ weekStart: IsoDateSchema })
    .strict();
export const RejectWeekRequestSchema = z
    .object({
    /** BR-PRJ-03-08: required, stamped on every entry. */
    comment: z.string().trim().min(1).max(2000),
})
    .strict();
export const CreateAdjustmentRequestSchema = z
    .object({
    projectId: z.uuid(),
    taskId: z.uuid(),
    workDate: IsoDateSchema,
    /** ± and never zero (BR-PRJ-03-10). */
    hours: SignedHoursStringSchema,
    reason: z.string().trim().min(1).max(500),
    /** The period it corrects. */
    adjustsWeekStart: IsoDateSchema,
})
    .strict();
export const timesheetsContract = c.router({
    week: {
        method: 'GET',
        path: '/api/timesheets/week',
        query: z
            .object({
            weekStart: IsoDateSchema,
            /** Somebody else's week needs projects.approve (BR-PRJ-03-15). */
            userId: z.uuid().optional(),
        })
            .strict(),
        responses: { 200: WeekResponseSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema },
        summary: "PRJ-03: one person's week — entries, §5c derived status, §5d locked, §5e missing working days. My week by default; a manager reads a submitted week to review it.",
    },
    upsertCell: {
        method: 'PUT',
        path: '/api/timesheets/entries',
        body: UpsertCellRequestSchema,
        responses: { 200: WeekResponseSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "The grid cell: sets the hours for that task and day. ZERO DELETES the entry (BR-PRJ-03-02); a new entry is a draft with the project's billable default unless overridden (BR-PRJ-03-03). Refused on a locked week (BR-PRJ-03-05).",
    },
    updateEntry: {
        method: 'PATCH',
        path: '/api/timesheets/entries/:entryId',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        body: UpdateEntryRequestSchema,
        responses: { 200: WeekResponseSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Edit a DRAFT entry — hours, notes and the ALWAYS-EDITABLE billable flag (BR-PRJ-03-03/-16). A locked entry answers the padlock sentence.',
    },
    deleteEntry: {
        method: 'DELETE',
        path: '/api/timesheets/entries/:entryId',
        pathParams: z.object({ entryId: z.uuid() }).strict(),
        responses: { 200: WeekResponseSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Delete a DRAFT entry (R8: drafts are freely deletable; nothing locked ever is).',
    },
    submitWeek: {
        method: 'POST',
        path: '/api/timesheets/weeks/submit',
        body: SubmitWeekRequestSchema,
        responses: { 200: WeekResponseSchema, 401: MessageSchema, 403: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-03-06: every draft entry of the week → submitted; the week locks at once (§5d). Refused when nothing is draft or the week is already locked.',
    },
    approvals: {
        method: 'GET',
        path: '/api/timesheets/approvals',
        responses: {
            200: z.object({ approvals: ApprovalRowSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: "The approvals queue (projects.approve): submitted weeks with their billable split — never the approver's own (BR-PRJ-03-14/-15).",
    },
    approveWeek: {
        method: 'POST',
        path: '/api/timesheets/weeks/:weekId/approve',
        pathParams: z.object({ weekId: z.uuid() }).strict(),
        body: z.object({}).strict().optional(),
        responses: { 200: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "BR-PRJ-03-07 (the confirm dialog is the client's; this is the commit): locks the week PERMANENTLY, entries immutable at the DB, actuals and the billing preview move. Self-approval refused (BR-PRJ-03-14).",
    },
    rejectWeek: {
        method: 'POST',
        path: '/api/timesheets/weeks/:weekId/reject',
        pathParams: z.object({ weekId: z.uuid() }).strict(),
        body: RejectWeekRequestSchema,
        responses: { 200: MessageSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-03-08: comment REQUIRED; every entry returns to Draft with the comment stamped on each; the week may be corrected and resubmitted.',
    },
    createAdjustment: {
        method: 'POST',
        path: '/api/timesheets/adjustments',
        body: CreateAdjustmentRequestSchema,
        responses: { 201: TimesheetEntrySchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-03-10/11 (the workflow, finally reachable): ± hours + a reason referencing a locked period; lands ALREADY APPROVED; corrects task actuals; NEVER billed (the fourth billing condition).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=timesheets.js.map